// app/components/landing/FeaturedProducts.tsx
"use client";

import Link from "next/link";
import useSWR from "swr";
import { motion } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { ProductProp } from "@/lib/types/productTypes";
import { axiosFetcher } from "@/lib/swrFetcher";

export default function ProductSection() {
  const { data, error, isLoading } = useSWR(
    "/products?status=ACTIVE&inStock=true&take=6&sortBy=popular",
    axiosFetcher,
    { revalidateOnFocus: false }
  );

  const products: ProductProp[] = data?.products || [];

  return (
    <section className="bg-[#F4F6F6] py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Product Section Header */}
        <div className="text-center mb-12">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm font-medium text-[#17BC2E] uppercase tracking-widest mb-3"
          >
            Trusted by thousands across Nigeria
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-[#080A09] text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
          >
            Top Rated Products
          </motion.h2>
        </div>

        {/* Loading skeleton */}
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="h-96 bg-gray-200 rounded-2xl animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Real products */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product, i) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07, duration: 0.5 }}
            >
              <Link
                href={`/product/${product.slug}`}
                className="group relative h-80 rounded-2xl overflow-hidden block"
              >
                <img
                  src={product.imageUrl || "/assets/img/fallback.jpg"}
                  alt={product.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  onError={(e) => { (e.target as HTMLImageElement).src = "/assets/img/fallback.jpg"; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <span className="text-xs font-medium text-white/70 uppercase tracking-wider">
                    {product.category.replace(/_/g, " ")}
                  </span>
                  <h3 className="text-base font-semibold mt-1 line-clamp-2 leading-snug">
                    {product.name}
                  </h3>
                  {product.rating > 0 && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <Star className="w-3.5 h-3.5 text-[#FFB600] fill-[#FFB600]" />
                      <span className="text-sm font-medium">{Number(product.rating).toFixed(1)}</span>
                      <span className="text-xs text-white/60">({product.reviewCount || 0})</span>
                    </div>
                  )}
                  <div className="mt-3 inline-flex items-center gap-1.5 bg-[#FFB600] text-black text-xs font-semibold px-4 py-1.5 rounded-full group-hover:bg-white transition-colors duration-300">
                    View Product <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link
            href="/marketplace"
            className="inline-flex items-center gap-2 bg-[#002301] text-white px-8 py-4 rounded-full hover:bg-[#001a00] hover:scale-105 transition-all font-semibold"
          >
            Explore Full Marketplace
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
